/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    SimpleGrid,
    Button,
    Center,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'

const SizeRefinements = ({filter, toggleFilter, selectedFilters, isMenu}) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant: 'square',
        disabled: false
    })
    const largestLength = Math.max(...filter.values.map((v) => v.label?.length || 0))
    return (
        <SimpleGrid
            templateColumns={{
                base: isMenu ? 'repeat(4, 79.75px)' : 'repeat(4, 79.75px)',
                lg: 'repeat(4, 79.75px)'
            }}
            spacing={isMenu ? '15px' : '10px'}
            ml={isMenu ? '10px' : 0}
            mt={{base: isMenu ? '20px' : '0', lg: '0'}}
            maxHeight="300px"
            overflowY="auto"
            sx={{
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                '&::-webkit-scrollbar': {
                    width: '4px',
                    height: '2px'
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'gray.800',
                    borderRadius: '2px',
                    width: '4px',
                    height: '2px',
                    display: 'block'
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'gray.400',
                    borderRadius: '2px',
                    width: '4px',
                    height: '2px',
                    display: 'block'
                },
                userSelect: 'none',
                scrollBehavior: 'smooth'
            }}
        >
            {filter.values?.map((value, idx) => {
                // Note the loose comparison, for "string == number" checks.
                const isSelected = selectedFilters.some((filterValue) => filterValue == value.value)
                // Don't display refinements with no results, unless we got there by selecting too
                // many refinements
                if (value.hitCount === 0 && !isSelected) return

                return (
                    <Button
                        key={idx}
                        {...styles.swatch}
                        backgroundColor={isSelected ? 'black.100' : 'gray.400'}
                        color={isSelected ? 'white' : 'black.100'}
                        aria-checked={isSelected}
                        border="none"
                        onClick={() => {
                            toggleFilter(value, filter.attributeId, isSelected)
                        }}
                        marginBottom={0}
                        marginRight={0}
                        borderRadius="3px"
                        aria-label={formatMessage(isSelected ? REMOVE_FILTER : ADD_FILTER, value)}
                        padding="0"
                        minWidth="79.75px"
                        minHeight="45px"
                        lineHeight="25px"
                    >
                        <Center
                            {...styles.swatchButton}
                            fontSize={largestLength > 8 ? '12px' : '16px'}
                        >
                            {value.label}
                        </Center>
                    </Button>
                )
            })}
        </SimpleGrid>
    )
}

SizeRefinements.propTypes = {
    filter: PropTypes.object,
    selectedFilters: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    toggleFilter: PropTypes.func,
    isMenu: PropTypes.bool
}

export default React.memo(SizeRefinements)
